import React, { useState } from 'react'
import { AlertTriangle, FileText, Loader2 } from 'lucide-react'
import { formatDisplayDateTime } from '@/lib/dateUtils'
import { Button } from './ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'
import { Checkbox } from './ui/checkbox'

export interface DuplicateFileInfo {
  id: string
  original_filename: string
  uploaded_at: string | null
  client_id: string | null
  transaction_count?: number
  has_committed_links?: boolean
  committed_link_count?: number
}

interface DuplicateFileDialogProps {
  open: boolean
  existingFile: DuplicateFileInfo | null
  pendingFileName: string
  onCancel: () => void
  onReprocess: (force: boolean) => Promise<void>
}

export const DuplicateFileDialog: React.FC<DuplicateFileDialogProps> = ({
  open,
  existingFile,
  pendingFileName,
  onCancel,
  onReprocess,
}) => {
  const [acknowledgeRisk, setAcknowledgeRisk] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)

  const hasCommittedLinks = existingFile?.has_committed_links ?? false
  const committedLinkCount = existingFile?.committed_link_count ?? 0
  const transactionCount = existingFile?.transaction_count ?? 0

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Unknown'
    return formatDisplayDateTime(dateString) || 'Unknown'
  }

  const handleReprocess = async () => {
    setIsProcessing(true)
    try {
      await onReprocess(hasCommittedLinks)
    } finally {
      setIsProcessing(false)
      setAcknowledgeRisk(false)
    }
  }

  const handleCancel = () => {
    setAcknowledgeRisk(false)
    onCancel()
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-500" />
            Duplicate File Detected
          </DialogTitle>
          <DialogDescription>
            This file has already been uploaded and processed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* File Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Existing File</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="text-gray-500">Filename:</div>
              <div className="font-medium truncate">{existingFile?.original_filename}</div>
              <div className="text-gray-500">Uploaded:</div>
              <div className="font-medium">{formatDate(existingFile?.uploaded_at ?? null)}</div>
              <div className="text-gray-500">Transactions:</div>
              <div className="font-medium">{transactionCount}</div>
            </div>
          </div>

          {/* Committed Links Warning */}
          {hasCommittedLinks && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-medium text-amber-800">
                    Warning: Committed to Mirage
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    {committedLinkCount} transaction(s) from this file have been committed to Mirage.
                    Deleting and reprocessing may cause data inconsistency or duplication in your
                    accounting system.
                  </p>

                  <div className="mt-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={acknowledgeRisk}
                        onCheckedChange={(checked) => setAcknowledgeRisk(checked === true)}
                        disabled={isProcessing}
                      />
                      <span className="text-sm text-amber-800">
                        I understand the risks and want to proceed
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Info for non-committed files */}
          {!hasCommittedLinks && transactionCount > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-700">
                Reprocessing will delete the existing {transactionCount} transaction(s)
                and process the file again. Any manual matches will be lost.
              </p>
            </div>
          )}

          {/* New file info */}
          <p className="text-sm text-gray-600">
            Would you like to delete the existing file and reprocess{' '}
            <strong>{pendingFileName}</strong>?
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleCancel} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleReprocess}
            disabled={isProcessing || (hasCommittedLinks && !acknowledgeRisk)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Processing...
              </>
            ) : (
              'Delete and Reprocess'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
